import {
  Button,
  Input,
  Loader2,
  Shield,
  User,
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AdminProfileFormData,
  adminProfileSchema,
} from "../schemas/adminAccount.schema";
import { userApi } from "../services/user.api";
import { useAppSelector } from "../store/hooks";
import { UserProfileDetails } from "../types/user.definition";
import { formatDateTime } from "../utils/date-format-utils";
import {
  civilityLabel,
  countryLabel,
  notificationMethodLabel,
  roleLabel,
} from "../utils/enum-label-utils";

interface DetailProps {
  label: string;
  value?: string | null;
}

const Detail: React.FC<DetailProps> = ({ label, value }) => (
  <div>
    <dt className="text-xs font-medium tracking-wide uppercase text-neutral-500">
      {label}
    </dt>
    <dd className="mt-1 text-sm text-neutral-900">{value || "—"}</dd>
  </div>
);

/**
 * MED-1448: the administrator's own profile, reachable from the header user
 * menu at `/admin/profile` — the entry used to point at `/manager/profile`,
 * a dashboard-app route that 404s here.
 *
 * Reads `GET /api/v1/users/{id}` for the logged-in administrator (the id
 * comes from the validated session, the same source the language switcher
 * already uses). The photo is submitted through
 * `PUT /api/v1/users/{id}/profile-image`; everything else is read-only
 * because `PUT /api/v1/users/{id}` takes the create-shaped `UserRequest`,
 * whose `password` is mandatory at the BFF and discarded by the API. Rather
 * than ship inputs that cannot save, those fields are displayed.
 */
export const AdminProfile: React.FC = () => {
  const { t } = useTranslation();
  const userId = useAppSelector(state => state.session.session?.user?.id);

  const [profile, setProfile] = useState<UserProfileDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<AdminProfileFormData>({
    resolver: zodResolver(adminProfileSchema(t)),
    mode: "onChange",
    defaultValues: { photoUrl: "" },
  });

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const details = await userApi.getUserById(userId);
      setProfile(details);
      reset({ photoUrl: details.photoUrl ?? "" });
    } catch {
      // The axios interceptor already surfaces the error globally.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [userId, reset]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (data: AdminProfileFormData) => {
    if (!userId) return;
    setSaving(true);
    try {
      const updated = await userApi.updateProfileImage(userId, data.photoUrl);
      setProfile(updated);
      reset({ photoUrl: updated.photoUrl ?? data.photoUrl });
      toast.success(t("admin.profile.photoSaved"));
    } catch {
      // Surfaced by the axios interceptor.
    } finally {
      setSaving(false);
    }
  };

  const photoUrl = watch("photoUrl");
  const primaryLocation =
    profile?.locations?.find(location => location.isPrimary) ??
    profile?.locations?.[0];
  const address = primaryLocation
    ? [
        primaryLocation.address,
        primaryLocation.city,
        primaryLocation.state,
        countryLabel(t, primaryLocation.country),
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  if (!userId) {
    return (
      <div className="p-6">
        <p className="text-sm text-error-600">{t("admin.profile.noSession")}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center space-x-4">
        <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-neutral-100">
          <User className="w-6 h-6 text-neutral-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            {t("admin.profile.title")}
          </h1>
          <p className="text-sm text-neutral-600">
            {t("admin.profile.subtitle")}
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center space-x-2 text-sm text-neutral-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t("common.loading")}</span>
        </div>
      )}

      {!loading && loadFailed && (
        <div className="p-4 bg-white rounded-xl border border-neutral-200">
          <p className="text-sm text-error-600">
            {t("admin.profile.loadError")}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-2 text-xs underline text-ews-primary"
          >
            {t("common.retry")}
          </button>
        </div>
      )}

      {!loading && profile && (
        <>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="p-5 space-y-4 bg-white rounded-xl border border-neutral-200"
          >
            <h2 className="text-lg font-semibold text-neutral-900">
              {t("admin.profile.photoTitle")}
            </h2>
            <div className="flex items-start space-x-4">
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={t("admin.profile.photoTitle")}
                  className="object-cover w-16 h-16 rounded-full"
                />
              ) : (
                <div className="flex justify-center items-center w-16 h-16 rounded-full bg-neutral-100">
                  <User className="w-6 h-6 text-neutral-400" />
                </div>
              )}
              <div className="flex-1">
                <Input
                  label={t("admin.profile.photoUrl")}
                  error={errors.photoUrl?.message}
                  fullWidth
                  {...register("photoUrl")}
                />
                <p className="mt-1 text-xs text-neutral-500">
                  {t("admin.profile.photoHint")}
                </p>
              </div>
            </div>
            <Button
              type="submit"
              loading={saving}
              disabled={saving || !isDirty}
            >
              {t("common.save")}
            </Button>
          </form>

          <div className="p-5 space-y-4 bg-white rounded-xl border border-neutral-200">
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4 text-neutral-500" />
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("admin.profile.detailsTitle")}
              </h2>
            </div>
            <p className="text-xs text-neutral-500">
              {t("admin.profile.readOnlyNotice")}
            </p>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Detail
                label={t("admin.profile.fields.civility")}
                value={civilityLabel(t, profile.civility)}
              />
              <Detail
                label={t("admin.profile.fields.role")}
                value={roleLabel(t, profile.roleName)}
              />
              <Detail
                label={t("admin.profile.fields.firstName")}
                value={profile.firstName}
              />
              <Detail
                label={t("admin.profile.fields.lastName")}
                value={profile.lastName}
              />
              <Detail
                label={t("admin.profile.fields.email")}
                value={profile.email}
              />
              <Detail
                label={t("admin.profile.fields.phoneNumber")}
                value={profile.phoneNumber}
              />
              <Detail
                label={t("admin.profile.fields.paymentPhoneNumber")}
                value={profile.paymentPhoneNumber}
              />
              <Detail
                label={t("admin.profile.fields.dateOfBirth")}
                value={
                  profile.dateOfBirth
                    ? formatDateTime(profile.dateOfBirth)
                    : undefined
                }
              />
              <Detail
                label={t("admin.profile.fields.notificationMethods")}
                value={(profile.preferredNotificationMethods ?? [])
                  .map(method => notificationMethodLabel(t, method))
                  .join(", ")}
              />
              <Detail
                label={t("admin.profile.fields.address")}
                value={address}
              />
            </dl>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminProfile;
